import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum as AssessmentStatus,
  UpdateAssessmentInput,
  UpdateAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  namedOperations,
  UpdateAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for updateAssessment.
 * Assessment has no schedule, so no schedule field transformation needed.
 */
type UpdateAssessmentTRPCInput = {
  Id: string;
  Title: string;
  Status: AssessmentStatus;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useUpdateAssessment = createMutationHook({
  name: 'updateAssessment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.update.mutationOptions({ onSuccess }),
  mapInput: (variables: UpdateAssessmentInput): UpdateAssessmentTRPCInput => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.assessment.register.queryKey(),
    trpc.frontend.assessment.getById.queryKey(),
    // COI-471 parity: Apollo twin evicts the assessment-result fields
    // (result ratings roll up into the assessment).
    trpc.frontend.documentAssessmentResult.pathKey(),
    trpc.frontend.obligationAssessmentResult.pathKey(),
    trpc.frontend.riskAssessmentResult.pathKey(),
  ],
  mapResponse: (): UpdateAssessmentMutation => ({
    updateAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateAssessmentDocument,
  graphqlVariables: (variables: UpdateAssessmentInput) => ({
    object: variables,
  }),
  evictFields: [
    'risk_assessment_result',
    'document_assessment_result',
    'obligation_assessment_result',
    'assessment',
  ],
  refetchQueries: [
    namedOperations.Query.getAssessmentById,
    namedOperations.Query.getAssessments,
  ],
});
